import React, { useState, useCallback, useEffect } from 'react'

interface RangeSliderProps {
  min: number
  max: number
  values: [number, number]
  onChange: (values: [number, number]) => void
  formatValue?: (value: number) => string
}

function RangeSliderComponent({ min, max, values, onChange, formatValue }: RangeSliderProps) {
  const [localValues, setLocalValues] = useState(values)

  // Update local values when prop values change
  useEffect(() => {
    setLocalValues(values)
  }, [values])

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Number(e.target.value)
      const newValues: [number, number] = [newMin, Math.max(newMin, localValues[1])]
      setLocalValues(newValues)
      onChange(newValues)
    },
    [localValues, onChange]
  )

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Number(e.target.value)
      const newValues: [number, number] = [Math.min(localValues[0], newMax), newMax]
      setLocalValues(newValues)
      onChange(newValues)
    },
    [localValues, onChange]
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          value={localValues[0]}
          onChange={handleMinChange}
          className="flex-1"
          aria-label="Minimum value"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={localValues[1]}
          onChange={handleMaxChange}
          className="flex-1"
          aria-label="Maximum value"
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>{formatValue ? formatValue(localValues[0]) : localValues[0]}</span>
        <span>{formatValue ? formatValue(localValues[1]) : localValues[1]}</span>
      </div>
    </div>
  )
}

// Memoize to prevent re-renders when parent updates
export const RangeSlider = React.memo(RangeSliderComponent)